import React from 'react';
import {
  Box,
  IconButton,
  useTheme as useMuiTheme,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../context/SidebarContext';
import { useThread } from '../../context/ThreadContext';
import { Z_INDEX, LAYOUT } from '../../constants/zIndex';
import UserMenu from './UserMenu';
import ReportFab from '../reports/ReportFab';

/**
 * MobileHeader Component - Fixed header for mobile views
 *
 * Structure:
 * - Left: Hamburger menu button
 * - Center: Logo
 * - Right: User avatar (compact UserMenu)
 *
 * Only renders on mobile devices when sidebar is hidden.
 */

const MobileHeader = () => {
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  const { openOverlay, isMobile } = useSidebar();
  const { handleNewThread, setPendingRolId, setAgentFilter } = useThread();

  // Handle logo click - navigate to home and create new conversation
  const handleLogoClick = () => {
    setAgentFilter(null);
    setPendingRolId(null);
    handleNewThread();
    navigate('/');
  };

  // Only render on mobile
  if (!isMobile) {
    return null;
  }

  return (
    <Box
      component="header"
      sx={{
        position: 'relative',
        minHeight: LAYOUT.MOBILE_HEADER_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // Brand purple background matching AIFlock theme
        background: 'linear-gradient(135deg, #9333EA 0%, #A855F7 100%)',
        // iOS safe area insets for all edges (notch, home indicator, curved screens)
        paddingTop: 'env(safe-area-inset-top)',
        paddingLeft: 'calc(12px + env(safe-area-inset-left))', // 12px base + safe area
        paddingRight: 'calc(12px + env(safe-area-inset-right))', // 12px base + safe area
        paddingBottom: '8px', // Vertical padding for content spacing
        borderBottom: 'none', // Remove border for cleaner gradient look
        zIndex: Z_INDEX.MOBILE_HEADER,
        boxShadow: '0 2px 8px rgba(147, 51, 234, 0.15)', // Subtle brand shadow
      }}
    >
      {/* Left: Hamburger menu */}
      <IconButton
        onClick={openOverlay}
        edge="start"
        aria-label="open menu"
        sx={{
          color: '#FFFFFF', // White for contrast on purple
          '@media (hover: hover) and (pointer: fine)': {
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
          },
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Center: AIFlock Logo */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          transition: 'opacity 0.2s ease',
          '&:active': { opacity: 0.7 }, // Touch feedback
          borderRadius: 1,
          p: 0.5,
          mx: -0.5,
        }}
        onClick={handleLogoClick}
        role="button"
        aria-label="Start new conversation"
      >
        <Box
          component="img"
          src="/ai-flock-logo-white.png"
          alt="AIFlock"
          sx={{
            height: 24,
            width: 'auto',
            maxWidth: 180,
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Right: Feedback + User avatar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <ReportFab
          tooltipPlacement="bottom"
          sx={{
            color: 'rgba(255,255,255,0.85)',
            '&:hover': { color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.15)' },
          }}
        />
        <UserMenu size={32} compact />
      </Box>
    </Box>
  );
};

export default MobileHeader;
